// Importaciones de React y recursos.
import React, { useRef, useEffect, useState } from 'react';
import video1 from "../videos/dscyber.mp4"; // El video a reproducir.
import logo from '../img/logo.png'; // Logo que se muestra cuando el video está pausado.
import './stylevideo.css'; // Estilos personalizados para el reproductor.

/**
 * Componente VideoPlayer.
 * Implementa un reproductor de video personalizado con controles para play/pausa,
 * volumen, barra de progreso interactiva y modo de pantalla completa.
 */
export const VideoPlayer = () => {
  // --- REFERENCIAS A ELEMENTOS DEL DOM ---
  const videoRef = useRef(null); // Referencia al elemento <video>.
  const progressBarRef = useRef(null); // Referencia a la barra de progreso.
  const thumbRef = useRef(null); // Referencia al círculo indicador en la barra de progreso.

  // --- ESTADOS DEL REPRODUCTOR ---
  const [isPlaying, setIsPlaying] = useState(false); // ¿Está el video reproduciéndose?
  const [currentTime, setCurrentTime] = useState(0); // Tiempo actual de reproducción en segundos.
  const [duration, setDuration] = useState(0); // Duración total del video en segundos.
  const [volume, setVolume] = useState(1); // Nivel de volumen (de 0 a 1).
  const [isMuted, setIsMuted] = useState(false); // ¿Está el video silenciado?
  const [isFullscreen, setIsFullscreen] = useState(false); // ¿Está en modo pantalla completa?
  const [showVolumeSlider, setShowVolumeSlider] = useState(false); // Controla la visibilidad del slider de volumen.
  const [isHoveringProgress, setIsHoveringProgress] = useState(false); // ¿Está el cursor sobre la barra de progreso?

  // --- EFECTO PRINCIPAL PARA SINCRONIZAR EL ESTADO DEL VIDEO CON LA UI ---
  useEffect(() => {
    const video = videoRef.current;

    // Funciones para actualizar la UI basadas en los eventos del video.
    const handleTimeUpdate = () => {
      const percentage = (video.currentTime / video.duration) * 100;
      progressBarRef.current.style.width = `${percentage}%`;
      thumbRef.current.style.left = `${percentage}%`;
      setCurrentTime(video.currentTime);
    };
    const handleLoadedMetadata = () => setDuration(video.duration);
    const handleVolumeChange = () => {
      setVolume(video.volume);
      setIsMuted(video.muted);
    };

    // Asigna los listeners de eventos al elemento de video.
    if (video) {
      video.addEventListener('timeupdate', handleTimeUpdate);
      video.addEventListener('loadedmetadata', handleLoadedMetadata);
      video.addEventListener('volumechange', handleVolumeChange);
    }

    // Limpieza: elimina los listeners cuando el componente se desmonta para evitar fugas de memoria.
    return () => {
      if (video) {
        video.removeEventListener('timeupdate', handleTimeUpdate);
        video.removeEventListener('loadedmetadata', handleLoadedMetadata);
        video.removeEventListener('volumechange', handleVolumeChange);
      }
    };
  }, []); // El array vacío `[]` asegura que este efecto se ejecute solo una vez (al montar y desmontar).

  // --- MANEJADORES DE EVENTOS DE LOS CONTROLES ---

  const handlePlayClick = () => {
    videoRef.current?.play();
    setIsPlaying(true);
  };

  const handlePauseClick = () => {
    videoRef.current?.pause();
    setIsPlaying(false);
  };

  // Alterna entre play y pausa al hacer clic en el video.
  const handleVideoClick = () => {
    isPlaying ? handlePauseClick() : handlePlayClick();
  };

  // Silencia o activa el sonido del video.
  const handleVolumeClick = () => {
    if (videoRef.current) {
      videoRef.current.muted = !videoRef.current.muted;
      setIsMuted(!isMuted);
    }
  };

  // Cambia el volumen del video según el valor del slider.
  const handleVolumeChange = (e) => {
    if (videoRef.current) {
      videoRef.current.volume = e.target.value;
      setVolume(e.target.value);
    }
  };

  // Activa o desactiva el modo de pantalla completa.
  const handleFullscreen = () => {
    if (videoRef.current) {
      if (!document.fullscreenElement) {
        videoRef.current.requestFullscreen();
        setIsFullscreen(true);
      } else {
        document.exitFullscreen();
        setIsFullscreen(false);
      }
    }
  };

  // Permite adelantar o retroceder el video al hacer clic en la barra de progreso.
  const handleProgressClick = (e) => {
    if (videoRef.current && progressBarRef.current) {
      const rect = e.currentTarget.getBoundingClientRect();
      const pos = (e.clientX - rect.left) / rect.width;
      videoRef.current.currentTime = pos * duration;
    }
  };
  
  // Muestra el tiempo exacto en la barra de progreso al pasar el cursor sobre ella.
  const handleProgressHover = (e) => {
    const progressBar = e.currentTarget;
    if (progressBar) {
      const rect = progressBar.getBoundingClientRect();
      const pos = (e.clientX - rect.left) / rect.width;
      const time = pos * duration;
      const timeElement = document.querySelector('.hover-time');
      if (timeElement) {
        timeElement.textContent = formatTime(time);
        timeElement.style.left = `${pos * 100}%`;
      }
    }
  };

  // --- FUNCIÓN UTILITARIA ---

  // Formatea el tiempo de segundos a un string "mm:ss".
  const formatTime = (time) => {
    if (isNaN(time)) return "0:00";
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  return (
    <div className='container-todo bg-[#061C5B] text-white p-6 mb-5 mt-5 rounded-lg shadow-xl'>
      <h2 className="text-4xl font-bold text-center mb-6">SEGUIMOS CRECIENDO</h2>
      
      <div className='container-video relative group'>
        <div className="video-container relative">
          <video 
            ref={videoRef} 
            className="carousel-video rounded-lg w-full cursor-pointer"
            onClick={handleVideoClick}
          >
            <source src={video1} type="video/mp4" />
            Tu navegador no soporta la etiqueta de video.
          </video>
          
          {/* Overlay con logo que aparece solo cuando el video está pausado. */}
          {!isPlaying && (
            <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 rounded-lg">
              <img 
                src={logo} 
                alt="Logo" 
                className="w-32 h-32 opacity-90 transition-opacity duration-300 hover:opacity-100 cursor-pointer" 
                onClick={handlePlayClick}
              />
            </div>
          )}

          {/* Controles personalizados del reproductor. */}
          <div className="custom-controls absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black to-transparent">
            {/* Barra de progreso interactiva. */}
            <div 
              className="progress-container relative mb-3"
              onMouseEnter={() => setIsHoveringProgress(true)}
              onMouseLeave={() => setIsHoveringProgress(false)}
              onMouseMove={handleProgressHover}
              onClick={handleProgressClick}
            >
              <div className="progress-bar bg-gray-600 bg-opacity-50 h-1.5 rounded-full cursor-pointer">
                <div className="progress bg-red-600 h-full rounded-full" ref={progressBarRef}></div>
                <div className={`thumb w-3 h-3 bg-red-600 rounded-full absolute top-1/2 transform -translate-y-1/2 transition-opacity duration-200 ${isHoveringProgress ? 'opacity-100' : 'opacity-0'}`} ref={thumbRef}></div>
              </div>
              {isHoveringProgress && (
                <div className="hover-time absolute bottom-full mb-2 transform -translate-x-1/2 text-xs bg-black bg-opacity-70 px-2 py-1 rounded">
                  {/* Este tiempo se actualiza por JS en handleProgressHover */}
                </div>
              )}
            </div>
            
            {/* Contenedores de botones (play, volumen, tiempo, fullscreen). */}
            <div className="flex items-center justify-between px-2">
              <div className="flex items-center space-x-4">
                {/* Botón Play/Pausa */}
                <button onClick={isPlaying ? handlePauseClick : handlePlayClick}>
                  {isPlaying ? (
                    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24"><path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" /></svg>
                  ) : (
                    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z" /></svg>
                  )}
                </button>
                
                {/* Control de Volumen */}
                <div className="relative" onMouseEnter={() => setShowVolumeSlider(true)} onMouseLeave={() => setShowVolumeSlider(false)}>
                  <button onClick={handleVolumeClick}>
                    {isMuted ? (
                      <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24"><path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z" /></svg>
                    ) : (
                      <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24"><path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z" /></svg>
                    )}
                  </button>
                  {showVolumeSlider && (
                    <div className="absolute left-0 bottom-full mb-2 bg-black bg-opacity-70 p-2 rounded">
                      <input type="range" min="0" max="1" step="0.1" value={volume} onChange={handleVolumeChange} className="w-24 accent-white"/>
                    </div>
                  )}
                </div>

                {/* Indicador de Tiempo */}
                <span className="text-white text-sm font-medium">
                  {formatTime(currentTime)} / {formatTime(duration)}
                </span>
              </div>
              
              {/* Botón de Pantalla Completa */}
              <button onClick={handleFullscreen}>
                {isFullscreen ? (
                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24"><path d="M5 16h3v3h2v-5H5v2zm3-8H5v2h5V5H8v3zm6 11h2v-3h3v-2h-5v5zm2-11V5h-2v5h5V8h-3z" /></svg>
                ) : (
                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24"><path d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z" /></svg>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VideoPlayer;
